/**
 * Administración de Plantillas de Contratos
 * Fecha: 16 Oct 2025
 * 
 * Vista CRUD completa para gestión de plantillas de contratos con editor
 * de texto enriquecido, variables y preview
 */

import React, { useState, useEffect } from 'react';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import SelectBuscable from '@/shared/components/SelectBuscable'
import { contractTemplatesService } from '@/features/rh/api/contractTemplates.service';
import type { ContractTemplate, TipoContrato } from '@/types/employee.types';
import {
  LoadingSpinner,
  Badge,
  Modal,
  ConfirmModal,
  PermissionGuard,
} from '@/shared/components';
import {
  Plus,
  Pen,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  Code,
  AlertCircle,
} from 'lucide-react';
import { EMPLOYEE_PERMISSIONS } from '@/shared/hooks/usePermissions';
import toast from 'react-hot-toast';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';

const TIPOS_CONTRATO: { value: TipoContrato; label: string }[] = [
  { value: 'tiempo_completo', label: 'Tiempo completo' },
  { value: 'medio_tiempo', label: 'Medio tiempo' },
  { value: 'por_proyecto', label: 'Por proyecto' },
  { value: 'por_horas', label: 'Por horas' },
];

const AVAILABLE_VARIABLES = [
  { key: 'nombre', label: 'Nombre completo', example: 'Juan Pérez García' },
  { key: 'curp', label: 'CURP', example: 'PEGJ900101HDFRNN00' },
  { key: 'rfc', label: 'RFC', example: 'PEGJ900101ABC' },
  { key: 'nss', label: 'Número de Seguro Social', example: '12345678901' },
  { key: 'puesto', label: 'Puesto', example: 'Desarrollador Senior' },
  { key: 'area', label: 'Departamento/Área', example: 'Tecnología' },
  { key: 'sueldo', label: 'Sueldo', example: '$25,000.00' },
  { key: 'fechaIngreso', label: 'Fecha de ingreso', example: '01/01/2025' },
  { key: 'fechaActual', label: 'Fecha actual', example: '16/10/2025' },
];

export const ContractTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipoContrato, setFilterTipoContrato] = useState<string>('');

  // Create/Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<ContractTemplate | null>(null);
  const [formNombre, setFormNombre] = useState('');
  const [formTipoContrato, setFormTipoContrato] = useState<TipoContrato>('tiempo_completo');
  const [formContenido, setFormContenido] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Variables Help Modal
  const [showVariablesModal, setShowVariablesModal] = useState(false);

  // Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<ContractTemplate | null>(null);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState<ContractTemplate | null>(null);

  // Activate/Deactivate Modals
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateTemplate, setActivateTemplate] = useState<ContractTemplate | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTemplate, setDeactivateTemplate] = useState<ContractTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await contractTemplatesService.getAll();
      setTemplates(data);
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditTemplate(null);
    setFormNombre('');
    setFormTipoContrato('tiempo_completo');
    setFormContenido('');
    setShowEditModal(true);
  };

  const handleEditClick = (template: ContractTemplate) => {
    setEditTemplate(template);
    setFormNombre(template.nombre);
    setFormTipoContrato(template.tipoContrato);
    setFormContenido(template.contenidoPlantilla);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formNombre.trim() || !formContenido.trim()) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    try {
      setIsSaving(true);

      if (editTemplate) {
        // Update
        await contractTemplatesService.update(editTemplate.id, {
          nombre: formNombre,
          tipoContrato: formTipoContrato,
          contenidoPlantilla: formContenido,
        });
      } else {
        // Create
        await contractTemplatesService.create({
          nombre: formNombre,
          tipoContrato: formTipoContrato,
          contenidoPlantilla: formContenido,
        });
      }

      await loadTemplates();
      setShowEditModal(false);
    } catch (error) {
      console.error('Error al guardar plantilla:', error);
      toast.error('Error al guardar la plantilla');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (template: ContractTemplate) => {
    setDeleteTemplate(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTemplate) return;

    try {
      await contractTemplatesService.deactivate(deleteTemplate.id);
      await loadTemplates();
      setShowDeleteModal(false);
      setDeleteTemplate(null);
    } catch (error) {
      console.error('Error al eliminar plantilla:', error);
      toast.error('Error al eliminar la plantilla');
    }
  };

  const handleActivateClick = (template: ContractTemplate) => {
    setActivateTemplate(template);
    setShowActivateModal(true);
  };

  const handleConfirmActivate = async () => {
    if (!activateTemplate) return;

    try {
      await contractTemplatesService.activate(activateTemplate.id);
      await loadTemplates();
      setShowActivateModal(false);
      setActivateTemplate(null);
    } catch (error) {
      console.error('Error al activar plantilla:', error);
      toast.error('Error al activar la plantilla');
    }
  };

  const handleDeactivateClick = (template: ContractTemplate) => {
    setDeactivateTemplate(template);
    setShowDeactivateModal(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTemplate) return;

    try {
      await contractTemplatesService.deactivate(deactivateTemplate.id);
      await loadTemplates();
      setShowDeactivateModal(false);
      setDeactivateTemplate(null);
    } catch (error) {
      console.error('Error al desactivar plantilla:', error);
      toast.error('Error al desactivar la plantilla');
    }
  };

  const handlePreviewClick = (template: ContractTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const handleInsertVariable = (varKey: string) => {
    setFormContenido((prev: string) => prev + `{{${varKey}}}`);
    setShowVariablesModal(false);
  };

  // Filters
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterTipoContrato || template.tipoContrato === filterTipoContrato;
    return matchesSearch && matchesType;
  });

  // Stats
  const totalTemplates = templates.length;
  const activeTemplates = templates.filter((t) => t.activo).length;
  const inactiveTemplates = totalTemplates - activeTemplates;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Plantillas de Contratos
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Administra las plantillas utilizadas para generar contratos
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_CREATE}>
            <button
              onClick={handleCreateClick}
              className={estiloDeBoton('primario')}
            >
              <Plus className="w-4 h-4" />
              Crear Plantilla
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard title="Total" value={totalTemplates} icon={<FileText />} variant="primary" />
        <KpiCard title="Activas" value={activeTemplates} icon={<CheckCircle />} variant="success" />
        <KpiCard title="Inactivas" value={inactiveTemplates} icon={<XCircle />} variant="secondary" />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Contrato
            </label>
            <SelectBuscable
              variante="listado"
              todos="Todos los tipos"
              value={filterTipoContrato}
              onChange={setFilterTipoContrato}
              opciones={TIPOS_CONTRATO.map((tipo) => ({ value: tipo.value, label: tipo.label }))}
            />
          </div>
        </div>
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-12 border border-gray-200 dark:border-gray-700 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No se encontraron plantillas
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {template.nombre}
                    </h3>
                    <Badge color={template.activo ? 'verde' : 'gris'}>
                      {template.activo ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <Badge color="azul">
                      {TIPOS_CONTRATO.find((t) => t.value === template.tipoContrato)?.label || template.tipoContrato}
                    </Badge>
                    <span>ID: {template.id}</span>
                    <span>Actualizada: {new Date(template.updatedAt).toLocaleDateString('es-MX')}</span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 line-clamp-2">
                    {template.contenidoPlantilla.substring(0, 200)}...
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handlePreviewClick(template)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                    title="Vista previa"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {template.activo ? (
                    <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_UPDATE}>
                      <button
                        onClick={() => handleDeactivateClick(template)}
                        className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-md"
                        title="Desactivar"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </PermissionGuard>
                  ) : (
                    <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_UPDATE}>
                      <button
                        onClick={() => handleActivateClick(template)}
                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md"
                        title="Activar"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </PermissionGuard>
                  )}

                  <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_UPDATE}>
                    <button
                      onClick={() => handleEditClick(template)}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md"
                      title="Editar"
                    >
                      <Pen className="h-4 w-4" />
                    </button>
                  </PermissionGuard>

                  <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_DELETE}>
                    <button
                      onClick={() => handleDeleteClick(template)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={editTemplate ? 'Editar Plantilla' : 'Crear Plantilla'}
        size="xl"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formNombre}
              onChange={(e) => setFormNombre(e.target.value)}
              placeholder="Ej: Contrato de Tiempo Indeterminado"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Contrato <span className="text-red-500">*</span>
            </label>
            <SelectBuscable
              variante="formulario"
              value={formTipoContrato}
              onChange={(valor) => setFormTipoContrato(valor as TipoContrato)}
              canDelete={false}
              opciones={TIPOS_CONTRATO.map((tipo) => ({ value: tipo.value, label: tipo.label }))}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Contenido del Contrato <span className="text-red-500">*</span>
              </label>
              <button
                onClick={() => setShowVariablesModal(true)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                <Code className="w-3 h-3" />
                Ver Variables
              </button>
            </div>
            <textarea
              value={formContenido}
              onChange={(e) => setFormContenido(e.target.value)}
              rows={12}
              placeholder="Contenido HTML del contrato. Usa {{variable}} para insertar datos dinámicos."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {'Usa las variables entre llaves dobles, por ejemplo: {{nombre}}, {{puesto}}, {{sueldo}}'}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowEditModal(false)}
              disabled={isSaving}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              {isSaving && <LoadingSpinner size="sm" />}
              {editTemplate ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Variables Help Modal */}
      <Modal
        isOpen={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
        title="Variables Disponibles"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-bold mb-1">¿Cómo usar las variables?</p>
                <p className="font-semibold leading-relaxed">
                  {'Inserta las variables en tu plantilla usando la sintaxis {{variable}}. Al generar el contrato, estas se reemplazarán con los datos reales del empleado.'}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {AVAILABLE_VARIABLES.map((variable) => (
              <div
                key={variable.key}
                className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() => handleInsertVariable(variable.key)}
              >
                <Code className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <code className="text-sm font-mono bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                    {`{{${variable.key}}}`}
                  </code>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                    {variable.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Ejemplo: {variable.example}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() => setShowVariablesModal(false)}
              className={estiloDeBoton('secundario')}
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      {previewTemplate && (
        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title="Vista Previa"
          size="xl"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {previewTemplate.nombre}
                </h3>
                <Badge color="azul" className="mt-2">
                  {TIPOS_CONTRATO.find((t) => t.value === previewTemplate.tipoContrato)?.label || previewTemplate.tipoContrato}
                </Badge>
              </div>
              <Badge color={previewTemplate.activo ? 'verde' : 'gris'}>
                {previewTemplate.activo ? 'Activa' : 'Inactiva'}
              </Badge>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-sm text-orange-700 font-semibold leading-relaxed">
                {'Las variables (como {{variable}}) se reemplazarán con datos reales del empleado al generar el contrato.'}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 max-h-96 overflow-y-auto">
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: previewTemplate.contenidoPlantilla }}
              />
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowPreviewModal(false)}
                className={estiloDeBoton('secundario')}
              >
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Activate Confirmation Modal */}
      <ConfirmModal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        onConfirm={handleConfirmActivate}
        title="Activar Plantilla"
        message={`¿Estás seguro de que deseas activar la plantilla "${activateTemplate?.nombre}"?`}
        confirmText="Activar"
      />

      {/* Deactivate Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleConfirmDeactivate}
        title="Desactivar Plantilla"
        message={`¿Estás seguro de que deseas desactivar la plantilla "${deactivateTemplate?.nombre}"? No podrá usarse para generar nuevos contratos.`}
        confirmText="Desactivar"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar Plantilla"
        message={`¿Estás seguro de que deseas eliminar la plantilla "${deleteTemplate?.nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
      />
    </div>
  );
};
